/** Publishes the matched route's `handle` into the document-title store.
 *
 *  Renders nothing, and is mounted as a SIBLING in `RootLayout` rather than as
 *  a hook in its body: `useMatches()` subscribes its caller to router state,
 *  and `RootLayout` currently re-renders exactly once for the app's lifetime —
 *  no reason to re-render the whole provider stack on every navigation.
 *
 *  It must also sit ABOVE `AuthProvider`, which renders a spinner instead of
 *  its children until the session round-trip finishes; below it, a cold load
 *  would keep index.html's title for the whole of hydration.
 */
import { useEffect } from 'react';
import { useLocation, useMatches } from 'react-router-dom';

import { publishRouteTitle, type RouteTitleHandle } from '@/utils/documentTitle';

function asTitleHandle(handle: unknown): RouteTitleHandle | null {
  if (!handle || typeof handle !== 'object') return null;
  const h = handle as RouteTitleHandle;
  return typeof h.title === 'string' && h.title.trim() ? h : null;
}

/** Route params arrive percent-encoded (ext-ids ids are composite strings). */
function decodeParam(raw: string | undefined): string | null {
  if (!raw) return null;
  try {
    return decodeURIComponent(raw);
  } catch {
    return raw;
  }
}

export function DocumentTitle() {
  const matches = useMatches();
  // The store is keyed on the LOCATION pathname, which is also what PageHeader
  // publishes. `match.pathname` is the matched prefix — for the splat route
  // `tour-operators/:id/*` it omits the tab, and the two writers would then
  // key on different strings and reset each other forever.
  const { pathname } = useLocation();

  // Deepest match that declares a title; layout routes declare none.
  let handle: RouteTitleHandle | null = null;
  let params: Readonly<Record<string, string | undefined>> = {};
  for (const match of matches) {
    const candidate = asTitleHandle(match.handle);
    if (candidate) {
      handle = candidate;
      params = match.params;
    }
  }

  const page = handle?.title ?? null;
  const detail = handle?.detail ?? false;
  const recordLabel = handle?.recordLabel ?? null;
  const id = detail || recordLabel ? decodeParam(params[handle?.idParam ?? 'id']) : null;
  // A default tab has no URL segment, so `/bookings/12` must not read
  // "Booking · Overview" while `/bookings/12/diagnose` does.
  const hasSection = Boolean(handle?.sectionParam && params[handle.sectionParam]?.trim());

  useEffect(() => {
    publishRouteTitle(pathname, { page, id, detail, recordLabel, hasSection });
  }, [pathname, page, id, detail, recordLabel, hasSection]);

  return null;
}
